import { FC, Fragment, useCallback } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { TrashIcon } from '@/atoms/Icons/DeleteIcon'
import { SceneIcon } from '@/atoms/Icons/SceneIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { CaptionLG, CaptionMD, CaptionSM, ParagraphMD, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useCollectiblesPurchaseContext } from '@/contexts/CollectiblesPurchaseContext/CollectiblesPurchaseContextProvider'
import { formatPrice } from '@/services/PriceFormatter'
import { CollectibleOffer, CollectibleProductInput, CollectibleProductPack } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'

interface Props {
  projectId: string
  className?: string
  checkoutLink?: string
}

export const CollectiblesSideCart: FC<Props> = ({ className, checkoutLink, projectId }) => {
  const {
    showSideCart,
    setShowSideCart,
    momentsInCart,
    packsInCart,
    collectibleTotal,
    cartItems,
    // resetCartState,
    removeFromCart,
    removePackFromCart,
  } = useCollectiblesPurchaseContext()
  const { t } = useTranslate('collectibles')

  const closeSideCart = useCallback(() => {
    setShowSideCart(false)
  }, [setShowSideCart])

  const getProductPrice = (offer: CollectibleOffer) => {
    const productId = offer?.products?.[0]?.id
    const cartItem = cartItems.find((item) => item.offerId === offer?.id)
    const matchingProduct = cartItem?.products?.find((p) => (p as CollectibleProductInput)?.productId === productId)
    return (matchingProduct as CollectibleProductPack)?.price
  }

  const handleRemoveAttachedMoments = useCallback(
    (productId: string, offerId: string) => {
      if (momentsInCart.length) {
        const matchingMoment = momentsInCart.find((moment) => moment.defaultOffer?.id === offerId)
        if (matchingMoment) {
          removeFromCart(Number(matchingMoment.id), offerId)
        }
      }
      removePackFromCart(productId, offerId)
    },
    [momentsInCart, removeFromCart, removePackFromCart],
  )

  return (
    <>
      <Transition.Root show={showSideCart} as={Fragment}>
        <Dialog
          as="div"
          className={classNames('absolute right-0 z-50 flex w-full h-full flex-row-reverse', className)}
          onClose={setShowSideCart}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-lg font-medium text-core-gray-900">
                            {t('collectibleScenes', 'Collectible Scenes')}
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="relative -m-2 p-2 text-core-gray-400 hover:text-core-gray-500"
                              onClick={closeSideCart}
                            >
                              <span className="absolute -inset-0.5" />
                              <span className="sr-only">{t('close', 'Close')}</span>
                              <CloseIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>

                        <div className="mt-8">
                          <div className="flow-root">
                            <ul role="list" className="-my-6 divide-y divide-core-gray-200">
                              {momentsInCart.map((moment) => {
                                const isGoldenScene = getIsGoldenScene(moment.type)
                                const price = getProductPrice(moment.defaultOffer ?? '') ?? 0

                                return (
                                  <div
                                    key={moment.id}
                                    className="my-3 grid h-full w-full grid-cols-[160px_1fr] gap-x-4 pt-2"
                                  >
                                    <div className="w-40 min-w-[160px]">
                                      <div className="aspect-h-8 aspect-w-16 relative my-1">
                                        <Image
                                          src={moment.thumbnail}
                                          alt={moment.title || 'Moment frame'}
                                          fill={true}
                                          className={classNames(
                                            'rounded-xl cursor-pointer outline-4 outline-transparent object-cover z-0',
                                          )}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex flex-col">
                                      <TitleXS className="mt-2">{moment.title}</TitleXS>
                                      <div className="flex justify-between">
                                        <div
                                          className={classNames(
                                            isGoldenScene ? 'text-warm-03' : 'text-secondary-cool',
                                            'flex items-center',
                                          )}
                                        >
                                          <SceneIcon size={18} color2={isGoldenScene ? 'warm-03' : 'secondary-cool'} />
                                          <CaptionSM className="ml-2 capitalize">
                                            {isGoldenScene ? t('golden', 'Golden') : t('collectible', 'Collectible')}{' '}
                                            {t('scene', 'Scene')}
                                          </CaptionSM>
                                        </div>
                                      </div>
                                      <div className="my-3 flex justify-between">
                                        <ParagraphMD>
                                          {formatPrice(price, { currency: 'USD', ignoreEmptyDecimal: true })}
                                        </ParagraphMD>
                                        <div></div>
                                        <div className="flex items-center">
                                          <div
                                            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-core-gray-200"
                                            onClick={() =>
                                              removeFromCart(Number(moment.id), moment.defaultOffer?.id ?? '')
                                            }
                                            // onClick={handleProductRemoval}
                                          >
                                            <TrashIcon size={18} color="core-gray-950" className="rounded-full" />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </ul>
                            <ul role="list" className="-my-6 divide-y divide-core-gray-200">
                              {packsInCart.map((pack) => {
                                return (
                                  <div
                                    key={pack.productId}
                                    className="my-3 grid h-full w-full grid-cols-[160px_1fr] gap-x-4 pt-2"
                                  >
                                    <div className="w-40 min-w-[160px]">
                                      <div className="aspect-h-8 aspect-w-16 relative my-1">
                                        <Image
                                          src={pack.image}
                                          alt={pack.name || 'Moment frame'}
                                          fill={true}
                                          className={classNames(
                                            'cursor-pointer outline-4 outline-transparent object-cover z-0',
                                          )}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex flex-col">
                                      <TitleXS className="mt-2">{pack.name}</TitleXS>
                                      <div className="my-3 flex justify-between">
                                        <ParagraphMD>
                                          {formatPrice(pack.price, { currency: 'USD', ignoreEmptyDecimal: true })}
                                        </ParagraphMD>
                                        <div></div>
                                        <div className="flex items-center">
                                          <div
                                            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-core-gray-200"
                                            onClick={() => handleRemoveAttachedMoments(pack.productId, pack.offerId)}
                                            // onClick={handleProductRemoval}
                                          >
                                            <TrashIcon size={18} color="core-gray-950" className="rounded-full" />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </ul>
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-core-gray-200 px-4 py-6 sm:px-6">
                        <div className="flex justify-between text-base font-medium text-core-gray-900">
                          <CaptionLG>{t('subtotal', 'Subtotal')}</CaptionLG>
                          <CaptionLG>{collectibleTotal}</CaptionLG>
                        </div>
                        <CaptionMD color="core-gray-500" italic className="mt-0.5">
                          {t('taxesAtCheckout', 'Taxes calculated at checkout.')}
                        </CaptionMD>
                        <div className="mt-6">
                          <InternalLink
                            href={checkoutLink ?? paths.collectibles.checkout(projectId)}
                            onClick={closeSideCart}
                          >
                            <Button variant="oxide" className="my-5 w-full">
                              {t('continueToCheckout', 'Continue to Checkout')}
                            </Button>
                          </InternalLink>
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export const getIsGoldenScene = (m: string) => {
  if (m === 'GOLDEN') {
    return true
  } else {
    false
  }
}
